import { useState, useMemo } from 'react';
import { expenses, vendorBalances, type Expense, type ExpenseCategory, type VendorBalance } from '@/mocks/expenses';

const paymentMethods = ["Nakit", "Kredi Kartı", "Havale", "Otomatik Ödeme"];

// Toast
interface Toast {
  id: number;
  message: string;
  type: 'success' | 'error';
}

export default function GiderlerPage() {
  const [expenseCategories, setExpenseCategories] = useState<ExpenseCategory[]>(["Kira", "Personel", "Malzeme", "Reklam", "Fatura", "Temizlik / Bakım", "Diğer"]);
  const [categoryColors, setCategoryColors] = useState<Record<string, string>>({
    "Kira": "#8B5CF6",
    "Personel": "#3B82F6",
    "Malzeme": "#C9A962",
    "Reklam": "#F59E0B",
    "Fatura": "#10B981",
    "Temizlik / Bakım": "#06B6D4",
    "Diğer": "#9CA3AF",
  });
  const [newCategoryName, setNewCategoryName] = useState('');
  const [showCategoryModal, setShowCategoryModal] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [expenseList, setExpenseList] = useState<Expense[]>(expenses);
  const [vendorList, setVendorList] = useState<VendorBalance[]>(vendorBalances);
  const [modalOpen, setModalOpen] = useState(false);
  const [editExpense, setEditExpense] = useState<Expense | null>(null);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [activeTab, setActiveTab] = useState<'expenses' | 'vendors'>('expenses');

  // Vendor form
  const [vendorModalOpen, setVendorModalOpen] = useState(false);
  const [vendorForm, setVendorForm] = useState<Partial<VendorBalance>>({ vendor: '', totalBilled: 0, totalPaid: 0 });

  // Form state
  const [formDate, setFormDate] = useState('');
  const [formCategory, setFormCategory] = useState<ExpenseCategory>('Malzeme');
  const [formDesc, setFormDesc] = useState('');
  const [formAmount, setFormAmount] = useState('');
  const [formPayment, setFormPayment] = useState('Nakit');
  const [formVendor, setFormVendor] = useState('');
  const [formRecurring, setFormRecurring] = useState(false);
  const [formNotes, setFormNotes] = useState('');

  const openModal = (exp?: Expense) => {
    if (exp) {
      setEditExpense(exp);
      setFormDate(exp.date);
      setFormCategory(exp.category);
      setFormDesc(exp.description);
      setFormAmount(exp.amount.toString());
      setFormPayment(exp.paymentMethod);
      setFormVendor(exp.vendor);
      setFormRecurring(exp.recurring);
      setFormNotes(exp.notes);
    } else {
      setEditExpense(null);
      setFormDate(new Date().toISOString().split('T')[0]);
      setFormCategory(expenseCategories[0]);
      setFormDesc('');
      setFormAmount('');
      setFormPayment('Nakit');
      setFormVendor('');
      setFormRecurring(false);
      setFormNotes('');
    }
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditExpense(null);
  };

  const showToast = (message: string, type: 'success' | 'error') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3000);
  };

  const handleSave = () => {
    if (!formDate || !formDesc.trim() || !formAmount || Number(formAmount) <= 0 || !formVendor.trim()) {
      showToast('Lütfen tarih, açıklama, tutar ve tedarikçi alanlarını doldurun.', 'error');
      return;
    }

    const payload: Expense = {
      id: editExpense ? editExpense.id : `exp-${Date.now()}`,
      date: formDate,
      category: formCategory,
      description: formDesc.trim(),
      amount: Number(formAmount),
      paymentMethod: formPayment,
      vendor: formVendor.trim(),
      recurring: formRecurring,
      notes: formNotes.trim(),
    };

    if (editExpense) {
      setExpenseList(prev => prev.map(e => e.id === editExpense.id ? payload : e));
      showToast('Gider başarıyla güncellendi.', 'success');
    } else {
      setExpenseList(prev => [payload, ...prev]);
      showToast('Yeni gider başarıyla eklendi.', 'success');
    }
    closeModal();
  };

  const filtered = useMemo(() => {
    let data = [...expenseList];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      data = data.filter(e => e.description.toLowerCase().includes(q) || e.vendor.toLowerCase().includes(q));
    }
    if (categoryFilter !== 'all') data = data.filter(e => e.category === categoryFilter);
    return data.sort((a, b) => b.date.localeCompare(a.date));
  }, [expenseList, searchQuery, categoryFilter]);

  const stats = useMemo(() => {
    const total = filtered.reduce((sum, e) => sum + e.amount, 0);
    const byCategory: Record<string, number> = {};
    filtered.forEach(e => { byCategory[e.category] = (byCategory[e.category] || 0) + e.amount; });
    return { total, byCategory };
  }, [filtered]);

  const handleDelete = (id: string) => {
    if (confirm('Bu gideri silmek istediğinize emin misiniz?')) {
      setExpenseList(prev => prev.filter(e => e.id !== id));
      showToast('Gider silindi.', 'success');
    }
  };

  const handleAddCategory = () => {
    const name = newCategoryName.trim();
    if (!name) { showToast('Kategori adı girin', 'error'); return; }
    if (expenseCategories.includes(name as ExpenseCategory)) { showToast('Bu kategori zaten var', 'error'); return; }
    const colors = ['#8B5CF6', '#3B82F6', '#C9A962', '#F59E0B', '#10B981', '#06B6D4', '#EF4444', '#EC4899', '#14B8A6', '#6366F1'];
    const newColor = colors[expenseCategories.length % colors.length];
    setExpenseCategories(prev => [...prev, name as ExpenseCategory]);
    setCategoryColors(prev => ({ ...prev, [name]: newColor }));
    setNewCategoryName('');
    showToast(`"${name}" kategorisi eklendi`, 'success');
  };

  const handleDeleteCategory = (cat: string) => {
    const hasExpenses = expenseList.some(e => e.category === cat);
    if (hasExpenses) { showToast('Bu kategoride kayıt var, önce giderleri taşıyın', 'error'); return; }
    setExpenseCategories(prev => prev.filter(c => c !== cat));
    showToast(`"${cat}" kategorisi silindi`, 'success');
  };

  // Vendor handlers
  const openVendorModal = (v?: VendorBalance) => {
    if (v) setVendorForm({ ...v });
    else setVendorForm({ vendor: '', totalBilled: 0, totalPaid: 0 });
    setVendorModalOpen(true);
  };
  const closeVendorModal = () => setVendorModalOpen(false);
  const handleVendorSave = () => {
    if (!vendorForm.vendor?.trim()) { showToast('Firma adı girin', 'error'); return; }
    setVendorList(prev => {
      const existing = prev.findIndex(v => v.vendor === vendorForm.vendor);
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = vendorForm as VendorBalance;
        return updated;
      }
      return [{ vendor: vendorForm.vendor!, totalBilled: vendorForm.totalBilled || 0, totalPaid: vendorForm.totalPaid || 0 }, ...prev];
    });
    showToast('Firma kaydı güncellendi', 'success');
    closeVendorModal();
  };

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Toast notifications */}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        {toasts.map(t => (
          <div key={t.id} className="px-4 py-3 rounded-lg shadow-lg text-sm font-medium flex items-center gap-2 animate-slide-in" style={{ background: t.type === 'success' ? '#10B981' : '#EF4444', color: '#fff' }}>
            <div className="w-4 h-4 flex items-center justify-center"><i className={t.type === 'success' ? 'ri-check-line' : 'ri-error-warning-line'} /></div>
            {t.message}
          </div>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold font-display" style={{ color: 'var(--text-primary)' }}>Giderler</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>{filtered.length} gider kaydı</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setShowCategoryModal(true)} className="spa-btn-secondary text-sm flex items-center gap-2">
            <div className="w-4 h-4 flex items-center justify-center"><i className="ri-settings-3-line text-sm" /></div>
            Kategori Yönet
          </button>
          <button onClick={() => openModal()} className="spa-btn-primary text-sm flex items-center gap-2">
            <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line text-sm" /></div>
            Yeni Gider
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 rounded-full" style={{ background: 'var(--bg-tertiary)' }}>
        {[
          { id: 'expenses' as const, label: 'Gider Listesi' },
          { id: 'vendors' as const, label: 'Firma Borç / Alacak' },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className="px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer"
            style={activeTab === tab.id
              ? { background: 'linear-gradient(135deg, var(--gold), var(--gold-dark))', color: '#fff' }
              : { background: 'transparent', color: 'var(--text-secondary)' }
            }
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'expenses' && (
        <>
          {/* Total + Category Breakdown */}
          <div className="spa-card p-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-3">
              <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Toplam Gider</p>
              <p className="text-2xl font-bold font-display" style={{ color: 'var(--text-primary)' }}>₺{stats.total.toLocaleString('tr-TR')}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {expenseCategories.map(cat => {
                const amount = stats.byCategory[cat] || 0;
                if (amount === 0) return null;
                return (
                  <div key={cat} className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium" style={{ background: (categoryColors[cat] || '#9CA3AF') + '12', color: categoryColors[cat] || '#9CA3AF', border: `1px solid ${(categoryColors[cat] || '#9CA3AF')}20` }}>
                    <div className="w-2 h-2 rounded-full" style={{ background: categoryColors[cat] || '#9CA3AF' }} />
                    {cat}: ₺{amount.toLocaleString('tr-TR')}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Toolbar */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center"><i className="ri-search-line text-sm" style={{ color: 'var(--text-muted)' }} /></div>
              <input className="spa-input pl-10" placeholder="Açıklama veya tedarikçi ara..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
            </div>
            <select className="spa-input text-sm min-w-[150px]" value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}>
              <option value="all">Tüm Kategoriler</option>
              {expenseCategories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          {/* Table */}
          <div className="spa-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ background: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border-color)' }}>
                    {['Tarih', 'Kategori', 'Açıklama', 'Tutar', 'Ödeme', 'Tedarikçi', 'İşlemler'].map(h => (
                      <th key={h} className="px-3 py-3 text-left text-xs font-semibold whitespace-nowrap" style={{ color: 'var(--text-secondary)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(exp => (
                    <tr key={exp.id} className="border-b hover:bg-theme-hover transition-colors" style={{ borderColor: 'var(--border-color)' }}>
                      <td className="px-3 py-3 whitespace-nowrap" style={{ color: 'var(--text-primary)' }}>{exp.date}</td>
                      <td className="px-3 py-3">
                        <span className="spa-badge text-[10px]" style={{ background: (categoryColors[exp.category] || '#9CA3AF') + '12', color: categoryColors[exp.category] || '#9CA3AF' }}>{exp.category}</span>
                      </td>
                      <td className="px-3 py-3">
                        <p style={{ color: 'var(--text-primary)' }}>{exp.description}</p>
                        {exp.recurring && <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Tekrarlayan gider</p>}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap font-semibold" style={{ color: 'var(--text-primary)' }}>₺{exp.amount.toLocaleString('tr-TR')}</td>
                      <td className="px-3 py-3 whitespace-nowrap" style={{ color: 'var(--text-secondary)' }}>{exp.paymentMethod}</td>
                      <td className="px-3 py-3 whitespace-nowrap" style={{ color: 'var(--text-secondary)' }}>{exp.vendor}</td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-1">
                          <button onClick={() => openModal(exp)} className="w-7 h-7 rounded-md flex items-center justify-center cursor-pointer" style={{ background: 'rgba(59,130,246,0.12)' }} title="Düzenle"><i className="ri-pencil-line text-xs" style={{ color: '#3B82F6' }} /></button>
                          <button onClick={() => handleDelete(exp.id)} className="w-7 h-7 rounded-md flex items-center justify-center cursor-pointer" style={{ background: 'rgba(239,68,68,0.12)' }} title="Sil"><i className="ri-delete-bin-line text-xs" style={{ color: '#EF4444' }} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {filtered.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 space-y-3">
              <div className="w-14 h-14 flex items-center justify-center rounded-full" style={{ background: 'var(--bg-tertiary)' }}>
                <i className="ri-money-cny-circle-line text-xl" style={{ color: 'var(--text-muted)' }} />
              </div>
              <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Gider kaydı bulunamadı</p>
            </div>
          )}
        </>
      )}

      {activeTab === 'vendors' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{vendorList.length} firma kaydı</p>
            <button onClick={() => openVendorModal()} className="spa-btn-primary text-sm flex items-center gap-2">
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line text-sm" /></div>
              Yeni Firma
            </button>
          </div>

          <div className="spa-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ background: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border-color)' }}>
                    {['Firma', 'Toplam Fatura', 'Toplam Ödeme', 'Bakiye (Borç)', 'Durum', 'İşlemler'].map(h => (
                      <th key={h} className="px-3 py-3 text-left text-xs font-semibold whitespace-nowrap" style={{ color: 'var(--text-secondary)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {vendorList.map(v => {
                    const balance = v.totalBilled - v.totalPaid;
                    return (
                      <tr key={v.vendor} className="border-b hover:bg-theme-hover transition-colors" style={{ borderColor: 'var(--border-color)' }}>
                        <td className="px-3 py-3 font-medium" style={{ color: 'var(--text-primary)' }}>{v.vendor}</td>
                        <td className="px-3 py-3 whitespace-nowrap" style={{ color: 'var(--text-primary)' }}>₺{v.totalBilled.toLocaleString('tr-TR')}</td>
                        <td className="px-3 py-3 whitespace-nowrap" style={{ color: 'var(--text-secondary)' }}>₺{v.totalPaid.toLocaleString('tr-TR')}</td>
                        <td className="px-3 py-3 whitespace-nowrap font-semibold" style={{ color: balance > 0 ? '#EF4444' : '#10B981' }}>
                          ₺{balance.toLocaleString('tr-TR')}
                        </td>
                        <td className="px-3 py-3">
                          {balance > 0 ? (
                            <span className="spa-badge text-[10px]" style={{ background: 'rgba(239,68,68,0.12)', color: '#EF4444' }}>Borçlu</span>
                          ) : balance < 0 ? (
                            <span className="spa-badge text-[10px]" style={{ background: 'rgba(245,158,11,0.12)', color: '#F59E0B' }}>Alacaklı</span>
                          ) : (
                            <span className="spa-badge text-[10px]" style={{ background: 'rgba(16,185,129,0.12)', color: '#10B981' }}>Kapanmış</span>
                          )}
                        </td>
                        <td className="px-3 py-3">
                          <button onClick={() => openVendorModal(v)} className="w-7 h-7 rounded-md flex items-center justify-center cursor-pointer" style={{ background: 'rgba(59,130,246,0.12)' }} title="Düzenle">
                            <i className="ri-pencil-line text-xs" style={{ color: '#3B82F6' }} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {vendorList.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 space-y-3">
              <div className="w-14 h-14 flex items-center justify-center rounded-full" style={{ background: 'var(--bg-tertiary)' }}>
                <i className="ri-building-2-line text-xl" style={{ color: 'var(--text-muted)' }} />
              </div>
              <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Firma kaydı bulunamadı</p>
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.45)' }} onClick={closeModal}>
          <div className="spa-card w-full max-w-lg flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b flex-shrink-0" style={{ borderColor: 'var(--border-color)' }}>
              <h2 className="text-lg font-bold font-display" style={{ color: 'var(--text-primary)' }}>{editExpense ? 'Gideri Düzenle' : 'Yeni Gider Ekle'}</h2>
              <button onClick={closeModal} className="w-8 h-8 flex items-center justify-center rounded-md cursor-pointer" style={{ background: 'var(--bg-tertiary)' }}><i className="ri-close-line" style={{ color: 'var(--text-muted)' }} /></button>
            </div>
            <div className="p-4 space-y-4 overflow-y-auto flex-1 min-h-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium block mb-1.5" style={{ color: 'var(--text-secondary)' }}>Tarih <span style={{ color: '#EF4444' }}>*</span></label>
                  <input type="date" className="spa-input text-sm" value={formDate} onChange={e => setFormDate(e.target.value)} />
                </div>
                <div>
                  <label className="text-xs font-medium block mb-1.5" style={{ color: 'var(--text-secondary)' }}>Kategori <span style={{ color: '#EF4444' }}>*</span></label>
                  <select className="spa-input text-sm" value={formCategory} onChange={e => setFormCategory(e.target.value as ExpenseCategory)}>
                    {expenseCategories.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium block mb-1.5" style={{ color: 'var(--text-secondary)' }}>Açıklama <span style={{ color: '#EF4444' }}>*</span></label>
                <input className="spa-input text-sm" placeholder="Örn. Mayıs ayı kirası" value={formDesc} onChange={e => setFormDesc(e.target.value)} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium block mb-1.5" style={{ color: 'var(--text-secondary)' }}>Tutar (₺) <span style={{ color: '#EF4444' }}>*</span></label>
                  <input type="number" className="spa-input text-sm" placeholder="0" value={formAmount} onChange={e => setFormAmount(e.target.value)} />
                </div>
                <div>
                  <label className="text-xs font-medium block mb-1.5" style={{ color: 'var(--text-secondary)' }}>Ödeme Yöntemi <span style={{ color: '#EF4444' }}>*</span></label>
                  <select className="spa-input text-sm" value={formPayment} onChange={e => setFormPayment(e.target.value)}>
                    {paymentMethods.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium block mb-1.5" style={{ color: 'var(--text-secondary)' }}>Tedarikçi / Satıcı <span style={{ color: '#EF4444' }}>*</span></label>
                <input className="spa-input text-sm" placeholder="Örn. Etiler Plaza Yönetimi" value={formVendor} onChange={e => setFormVendor(e.target.value)} />
              </div>
              <div className="flex items-center gap-2">
                <input id="recurring" type="checkbox" className="w-4 h-4 rounded border cursor-pointer" style={{ accentColor: '#C9A962' }} checked={formRecurring} onChange={e => setFormRecurring(e.target.checked)} />
                <label htmlFor="recurring" className="text-sm cursor-pointer" style={{ color: 'var(--text-secondary)' }}>Tekrarlayan gider</label>
              </div>
              <div>
                <label className="text-xs font-medium block mb-1.5" style={{ color: 'var(--text-secondary)' }}>Notlar</label>
                <textarea className="spa-input text-sm min-h-[80px] resize-none" placeholder="Ek notlar..." maxLength={500} value={formNotes} onChange={e => setFormNotes(e.target.value)} />
                <p className="text-[10px] mt-1 text-right" style={{ color: 'var(--text-muted)' }}>{formNotes.length}/500</p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 p-4 border-t flex-shrink-0" style={{ borderColor: 'var(--border-color)' }}>
              <button onClick={closeModal} className="spa-btn-outline text-sm">İptal</button>
              <button onClick={handleSave} className="spa-btn-primary text-sm">{editExpense ? 'Güncelle' : 'Kaydet'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Vendor Modal */}
      {vendorModalOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.45)' }} onClick={closeVendorModal}>
          <div className="spa-card w-full max-w-md flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: 'var(--border-color)' }}>
              <h2 className="text-lg font-bold font-display" style={{ color: 'var(--text-primary)' }}>Firma Borç/Alacak</h2>
              <button onClick={closeVendorModal} className="w-8 h-8 flex items-center justify-center rounded-md cursor-pointer" style={{ background: 'var(--bg-tertiary)' }}><i className="ri-close-line" style={{ color: 'var(--text-muted)' }} /></button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="text-xs font-medium block mb-1.5" style={{ color: 'var(--text-secondary)' }}>Firma Adı <span style={{ color: '#EF4444' }}>*</span></label>
                <input className="spa-input text-sm" placeholder="Firma adı..." value={vendorForm.vendor || ''} onChange={e => setVendorForm(prev => ({ ...prev, vendor: e.target.value }))} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium block mb-1.5" style={{ color: 'var(--text-secondary)' }}>Toplam Fatura (₺)</label>
                  <input type="number" className="spa-input text-sm" placeholder="0" value={vendorForm.totalBilled || 0} onChange={e => setVendorForm(prev => ({ ...prev, totalBilled: Number(e.target.value) }))} />
                </div>
                <div>
                  <label className="text-xs font-medium block mb-1.5" style={{ color: 'var(--text-secondary)' }}>Toplam Ödeme (₺)</label>
                  <input type="number" className="spa-input text-sm" placeholder="0" value={vendorForm.totalPaid || 0} onChange={e => setVendorForm(prev => ({ ...prev, totalPaid: Number(e.target.value) }))} />
                </div>
              </div>
              <div className="flex items-center gap-2 p-3 rounded-spa-sm" style={{ background: 'var(--bg-tertiary)' }}>
                <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Bakiye:</span>
                <span className="text-sm font-semibold" style={{ color: ((vendorForm.totalBilled || 0) - (vendorForm.totalPaid || 0)) > 0 ? '#EF4444' : '#10B981' }}>
                  ₺{((vendorForm.totalBilled || 0) - (vendorForm.totalPaid || 0)).toLocaleString('tr-TR')}
                </span>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 p-4 border-t" style={{ borderColor: 'var(--border-color)' }}>
              <button onClick={closeVendorModal} className="spa-btn-outline text-sm">İptal</button>
              <button onClick={handleVendorSave} className="spa-btn-primary text-sm">Kaydet</button>
            </div>
          </div>
        </div>
      )}

      {/* Category Management Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto" style={{ background: 'rgba(0,0,0,0.45)' }} onClick={() => setShowCategoryModal(false)}>
          <div className="spa-card w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: 'var(--border-color)' }}>
              <h2 className="text-lg font-bold font-display" style={{ color: 'var(--text-primary)' }}>Kategori Yönetimi</h2>
              <button onClick={() => setShowCategoryModal(false)} className="w-8 h-8 flex items-center justify-center rounded-md cursor-pointer" style={{ background: 'var(--bg-tertiary)' }}><i className="ri-close-line" style={{ color: 'var(--text-muted)' }} /></button>
            </div>
            <div className="p-4 space-y-4">
              <div className="flex gap-2">
                <input
                  className="spa-input flex-1 text-sm"
                  placeholder="Yeni kategori adı..."
                  value={newCategoryName}
                  onChange={e => setNewCategoryName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleAddCategory(); }}
                />
                <button onClick={handleAddCategory} className="spa-btn-primary text-sm whitespace-nowrap">Ekle</button>
              </div>
              <div className="space-y-2">
                {expenseCategories.map(cat => (
                  <div key={cat} className="flex items-center justify-between p-3 rounded-spa-sm border" style={{ borderColor: 'var(--border-color)', background: 'var(--bg-tertiary)' }}>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ background: categoryColors[cat] || '#9CA3AF' }} />
                      <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{cat}</span>
                    </div>
                    <button
                      onClick={() => handleDeleteCategory(cat)}
                      className="w-7 h-7 rounded-md flex items-center justify-center cursor-pointer"
                      style={{ background: 'rgba(239,68,68,0.12)' }}
                      title="Sil"
                    >
                      <i className="ri-delete-bin-line text-xs" style={{ color: '#EF4444' }} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}